import React, { FC, PropsWithChildren, useContext, useMemo } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { useGetMomentsByEpisode } from '@/services/MomentsService'
import { Moment } from '@/types/codegen-federation'

interface PlayerMomentsContextProps {
  moments?: Moment[]
  canDisplayScenes: boolean
  getMoment: ({ time }: { time: Maybe<number> }) => Moment | null
}

type PlayerMomentsProviderProps = PropsWithChildren<{ episodeGuid: string }>

export const PlayerMomentsContext = React.createContext<PlayerMomentsContextProps>({
  getMoment: () => null,
  canDisplayScenes: false,
})

function findMomentByTime(moments: Moment[] = [], time?: number): Moment | null {
  if (!time || time <= 0) {
    return null
  }

  for (let i = 0; i < moments.length; i++) {
    // these checks include potential scene gaps
    for (let i = 0; i < moments?.length; i++) {
      const endOfMoments = i + 1 === moments.length
      const nextMoment = moments[i + 1]

      const isBeforeNextMoment = nextMoment?.startTime && time < nextMoment?.startTime
      if (isBeforeNextMoment || endOfMoments) {
        return moments[i]
      }
    }
  }
  return null
}

export const PlayerMomentsProvider: FC<PlayerMomentsProviderProps> = ({ children, episodeGuid }) => {
  const { data } = useGetMomentsByEpisode(episodeGuid)

  const value = useMemo(() => {
    const moments = (data?.episode?.moments || []) as NonNullable<Moment>[]

    const getMoment = ({ time }: { time: Maybe<number> }) => {
      return findMomentByTime(moments, time ?? 0)
    }

    const canDisplayScenes = (data?.episode?.displayScenePanel && !!moments.length) || false

    return {
      canDisplayScenes,
      moments,
      getMoment,
    }
  }, [data?.episode?.displayScenePanel, data?.episode?.moments])

  return <PlayerMomentsContext.Provider value={value}>{children}</PlayerMomentsContext.Provider>
}

PlayerMomentsContext.displayName = 'PlayerMomentsContext'

export const usePlayerMoments = () => {
  return useContext(PlayerMomentsContext)
}
